/**
 * Turns a parsed dispatch into load board values, using the app's own names
 * for terminals, destinations, brokers and drivers. Problems are reported as
 * review reasons rather than guessed at.
 */
import { DELIVERY_STATUSES, LoadFormData, TERMINALS } from '../../config/types';
import { BrokerKey, ParsedDispatch } from './parsers';
import { readSplit, SplitInfo } from './split';

export const IMPORT_DEFAULTS = {
  driver: 'Haitham',
  status: DELIVERY_STATUSES.PENDING,
  // Broker shortnames as stored in the `brokers` collection.
  brokers: { titan: 'Titan', avfuel: 'Avfuel' } as Record<BrokerKey, string>,
  // Forms that name the supplier or city instead of the terminal; every word
  // must appear. Titan's "Valero / Carson" is Avfuel's "Kinder Morgan, Carson"
  // (same Valero supplier and customer #366 on both).
  terminalAliases: [{ contains: ['valero', 'carson'], terminal: 'Kinder Morgan' }],
  // Known delivery points (Data Entry's customer list), matched on street
  // number + city so "San Bernardino Intl Airport, CA" files as the same place.
  destinations: [
    { name: 'Air Center San Diego', street: '1424', city: 'San Diego' },
    { name: 'Riverside Air Service', street: '6741', city: 'Riverside' },
    { name: 'Flyers Enterprises @ Oceanside', street: '110', city: 'Oceanside' },
    { name: 'Flyers Enterprises @ El Cajon', street: '1745', city: 'El Cajon' },
    { name: 'Archer Jet Center', street: '37232', city: 'Hawthorne' },
    { name: 'San Bernardino Airport', street: '2055', city: 'San Bernardino' },
    { name: 'Bakersfield Jet Center', street: '1601', city: 'Bakersfield' }
  ]
};

export interface DispatchDetails {
  deliveryWindow: string;
  products: string;
  supplier: string;
  terminalName: string;
  destinationName: string;
  destinationAddress: string;
  destinationPhone: string;
  /** The delivery point isn't on the known list yet */
  newDestination: boolean;
  poNumber: string;
  instructions: string;
  comments: string;
}

export interface ImportedLoad {
  /** Load board fields (minus the file, attached when saving) */
  load: Omit<LoadFormData, 'pdfFile' | 'pdfFileName'>;
  details: DispatchDetails;
  /** Set when this dispatch is one drop of a split load */
  split: SplitInfo | null;
  /** Fields that couldn't be read with certainty */
  reviewReasons: string[];
}

/** MM/DD/YYYY (or M/D/YY) to YYYY-MM-DD; '' when it isn't a real date. */
export const toIsoDate = (raw: string): string => {
  const match = (raw || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!match) return '';
  const month = Number(match[1]);
  const day = Number(match[2]);
  let year = Number(match[3]);
  if (year < 100) year += 2000;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
};

/** "8,000.00" to 8000; 0 when not a positive number. */
export const toGallons = (raw: string): number => {
  const value = parseFloat(String(raw || '').replace(/,/g, ''));
  return isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : 0;
};

/** The load board terminal for this pickup, or '' when it can't be told. */
export const resolveTerminal = (parsed: ParsedDispatch): string => {
  const haystack = [parsed.terminalName, parsed.terminalCity, parsed.supplier]
    .join(' ')
    .toLowerCase();
  const direct = TERMINALS.find(name => haystack.includes(name.toLowerCase()));
  if (direct) return direct;
  const alias = IMPORT_DEFAULTS.terminalAliases.find(rule =>
    rule.contains.every(word => haystack.includes(word))
  );
  return alias ? alias.terminal : '';
};

/**
 * The app's name for the delivery point; falls back to the form's own name
 * (tidied) for places not on the list yet.
 */
export const resolveDestination = (parsed: ParsedDispatch): { name: string; known: boolean } => {
  const street = (parsed.destinationAddress.match(/^\s*(\d+)/) || [])[1] || '';
  const city = parsed.destinationCityStateZip.split(',')[0].trim().toLowerCase();
  const known = IMPORT_DEFAULTS.destinations.find(
    place => place.street === street && place.city.toLowerCase() === city
  );
  if (known) return { name: known.name, known: true };
  const tidy = parsed.destinationName
    .replace(/,?\s*(Inc|LLC|Corp)\.?$/i, '')
    // "Van Nuys, CA 2" -> "Van Nuys 2", "Camarillo,CA" -> "Camarillo"
    .replace(/,\s*[A-Z]{2}\b/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { name: tidy, known: false };
};

/** Today's date, local to the browser, as YYYY-MM-DD. */
const today = (now: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Builds the load board values plus review flags for one parsed dispatch. */
export const toImportedLoad = (parsed: ParsedDispatch, now: Date = new Date()): ImportedLoad => {
  const reviewReasons: string[] = [];

  const loadNumber = (parsed.loadNumber || '').replace(/\s+/g, '');
  if (!/^[A-Za-z0-9-]+$/.test(loadNumber)) reviewReasons.push('load number');

  const deliveryDate = toIsoDate(parsed.deliveryDate);
  if (!deliveryDate) reviewReasons.push('delivery date');

  const totalGallons = parsed.products.reduce((sum, p) => sum + toGallons(p.gallons), 0);
  if (!totalGallons) reviewReasons.push('gallons');

  const terminal = resolveTerminal(parsed);
  if (!terminal) reviewReasons.push('terminal');

  const destination = resolveDestination(parsed);
  if (!destination.name) reviewReasons.push('destination');

  if (parsed.pumpRequired === null) reviewReasons.push('pump required');

  const split = readSplit({
    brokerKey: parsed.brokerKey,
    deliveryDate,
    destination: `${parsed.destinationName} ${destination.name}`,
    gallons: totalGallons,
    text: `${parsed.instructions} ${parsed.comments}`
  });

  return {
    reviewReasons,
    split,
    load: {
      broker: IMPORT_DEFAULTS.brokers[parsed.brokerKey],
      destination: destination.name,
      orderNumber: parsed.orderNumber || '',
      loadNumber,
      deliveryDate,
      gallons: totalGallons ? String(totalGallons) : '',
      pumpRequired: parsed.pumpRequired === true,
      status: IMPORT_DEFAULTS.status,
      terminal,
      driver: IMPORT_DEFAULTS.driver,
      currentDate: today(now)
    },
    details: {
      deliveryWindow: parsed.deliveryWindow || '',
      products: parsed.products.map(p => p.product).filter(Boolean).join(', '),
      supplier: parsed.supplier || '',
      terminalName: [parsed.terminalName, parsed.terminalCity]
        .filter((v, i, all) => v && all.indexOf(v) === i)
        .join(', '),
      destinationName: parsed.destinationName || '',
      destinationAddress: [parsed.destinationAddress, parsed.destinationCityStateZip]
        .filter(Boolean)
        .join(', '),
      destinationPhone: parsed.destinationPhone || '',
      newDestination: !destination.known,
      poNumber: parsed.poNumber || '',
      instructions: parsed.instructions || '',
      comments: parsed.comments || ''
    }
  };
};
